import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { all } from 'rsvp'
import { service } from '@ember/service'
import { isEqual, isEmpty } from '@ember/utils'
import { action } from '@ember/object'
import {
  optionTemplate as groupFilterDropdownOptionTemplate,
  optionTemplateSubLevel as groupFilterDropdownSubOptionTemplate,
} from 'teacher-ui-client/utils/dropdown-templates'
import { task } from 'ember-concurrency'
import { groupBy, unionBy, intersectionBy } from 'lodash'
import { runTask } from 'ember-lifeline'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { TABLE_THEMES } from 'teacher-ui-client/manage/table-themes'
import { subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import type { IntlService } from 'ember-intl'
import type StudentCsvMaker from 'teacher-ui-client/services/student-csv-maker'
import type FileDownload from 'teacher-ui-client/services/file-download'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type RouterService from '@ember/routing/router-service'
import type Store from '@ember-data/store'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { Log } from '@blakeelearning/log'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type ManageStudentsRoute from 'teacher-ui-client/manage/students/route'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Student from 'teacher-ui-client/models/student'
import {
  isOptionWithStudentCount,
  type SelectOptionWithStudentCount,
} from 'teacher-ui-client/components/form-components/form-select/option'
import { hasId } from 'teacher-ui-client/utils/type-guards'

type SchoolClassOption = SelectOptionWithStudentCount
type TeacherOption = SelectOptionWithStudentCount & { value: SchoolClassOption[]; studentIds: Set<string> }

function getSchoolClassAsOption(schoolClass: SchoolClass & { id: string }): SelectOptionWithStudentCount {
  return {
    uid: schoolClass.id,
    text: schoolClass.name,
    value: schoolClass.id,
    studentCount: schoolClass.students.length,
  }
}

export const FILTER_VALUES = {
  ALL: 'ALL',
}

export const TAB_VALUES = {
  ALL_STUDENTS: 'all_students',
  MY_STUDENTS: 'my_students',
}

/**
 * Enum for tri-state allocation status values.
 * @readonly
 * @enum {string}
 */
export const ALLOCATION_FILTER_VALUES = {
  /** show ALL students regardless of allocation status. */
  ALL: FILTER_VALUES.ALL,
  /** only shows students WITH a teacher/class. */
  ALLOCATED: 'ALLOCATED',
  /** only shows students WITHOUT a teacher/class. */
  UNALLOCATED: 'UNALLOCATED',
}

export default class ManageStudentsController extends Controller<ModelFor<ManageStudentsRoute>> {
  @service intl!: IntlService

  @service studentCsvMaker!: StudentCsvMaker

  @service fileDownload!: FileDownload

  @service resourceConflict!: ResourceConflictService

  @service router!: RouterService

  @service store!: Store

  @service flashQueue!: FlashQueueService

  @service subscriptionType!: SubscriptionTypeService

  @service log!: Log

  @service gradeSets!: GradeSetsService

  queryParams = [
    { sortCol: 'col' },
    { sortDir: 'dir' },
    { sortCompare: 'compare' },
    { currentPage: 'pageNumber' },
    { textFilter: 'filter' },
    { selectedIds: 'selected' },
    { allocationFilterValue: 'allocation' },
    { recordsPerPage: 'perPage' },
  ] as const

  @tracked
  sortCol = 'firstName'

  @tracked
  sortDir = SortDirection.Asc

  @tracked
  sortCompare = SortType.ByValue

  @tracked
  currentPage = 1

  @tracked
  recordsPerPage = 100

  @tracked
  textFilter = ''

  @tracked
  selectedIds: string[] = []

  @tracked _groupFilterOption?: SelectOptionWithStudentCount

  get dataOperationsConfig() {
    return buildDataOperationsConfigProxy(this)
  }
  get editingRecordId() {
    return this.router.currentRoute?.params?.student_id
  }

  get currentSubscriptionType() {
    return this.subscriptionType.current
  }

  get currentTeacher() {
    return this.model.currentTeacher
  }

  get currentSchool() {
    return this.model.currentSchool
  }

  get myStudents() {
    return this.currentSubscriptionType
      ? this.currentTeacher.studentsForSubscriptionType(this.currentSubscriptionType)
      : []
  }

  get allStudents() {
    return this.model.allStudents.slice()
  }

  get tableTheme() {
    return TABLE_THEMES[subscriptionTypeToThemeKey(this.currentSubscriptionType)]
  }

  get iconColorClass() {
    const classes = {
      [SubscriptionType.Reading]: 'text-oceany-blue-300',
      [SubscriptionType.Maths]: 'text-ms-green-300',
      [SubscriptionType.Writing]: 'text-wl-blue-300',
    }
    return classes[this.currentSubscriptionType]
  }

  /**
   * Computes which key to use on the student model when accessing the property that represents
   * the student's school classes joined with a comma and space between.
   */
  get joinedSchoolClassNamesKey() {
    switch (this.currentSubscriptionType) {
      case SubscriptionType.Reading:
        return 'readingSchoolClassNamesCommaSeparated'
      case SubscriptionType.Maths:
        return 'mathsSchoolClassNamesCommaSeparated'
      case SubscriptionType.Writing:
        return 'writingSchoolClassNamesCommaSeparated'
      default:
        return ''
    }
  }

  /**
   * Modals
   */
  @tracked
  addToClassModalOpen = false

  @tracked
  editPasswordModalOpen = false

  @tracked
  editGradeModalOpen = false

  @tracked
  removeFromAllClassesModalOpen = false

  @tracked
  deleteStudentsModalOpen = false

  /**
   * @property {String}
   * When subco, tabs appear. This property holds a string that represents the current tab.
   */
  @tracked
  currentTab = TAB_VALUES.ALL_STUDENTS

  /**
   * @property {Boolean}
   * This is whether to show the allocation filter or not.
   * The allocation filter has `allocated` `unallocated` or `both` as options.
   * It's only shown for subcos.
   */
  get showAllocationFilter() {
    return this.currentTab === TAB_VALUES.ALL_STUDENTS && this.isSubco
  }

  /**
   * @property {Array<StudentModel>}
   * List of the base students that the list uses. Depends on whether the logged in user is
   * a subco, and if so, it depends which tab is selected.
   */
  get students() {
    const allStudentsTab = this.currentTab === TAB_VALUES.ALL_STUDENTS
    if (this.isSubco && allStudentsTab) {
      return this.allStudents.filter(hasId)
    }
    return this.myStudents.filter(hasId)
  }

  get cannotPrintCertificates() {
    return this.students.length === 0
  }

  get searchLabel() {
    if (this.currentTab !== TAB_VALUES.ALL_STUDENTS) {
      return this.intl.t('searchClasses')
    }
    return this.intl.t('reporting.dropdownSearchTeachers')
  }

  /**
   * If the currently logged in teacher is a subco.
   */
  get isSubco() {
    return this.currentTeacher.subcoFor(this.currentSubscriptionType)
  }

  /**
   * Filtering properties for allocation filter
   */
  @tracked
  allocationFilterValue = ALLOCATION_FILTER_VALUES.ALL

  get allocationOptionSet() {
    return {
      id: 'allocation-filter',
      options: [
        {
          id: ALLOCATION_FILTER_VALUES.UNALLOCATED,
          label: this.intl.t('manage.manageClasses.students.unallocated'),
        },
        {
          id: ALLOCATION_FILTER_VALUES.ALLOCATED,
          label: this.intl.t('manage.manageClasses.students.allocated'),
        },
        {
          id: ALLOCATION_FILTER_VALUES.ALL,
          label: this.intl.t('manage.selectAllLabel'),
        },
      ],
    }
  }

  /**
   * These are subscribed students that are in any of the classes of the logged in teacher
   */
  get mySubscribedStudents() {
    const myStudents = this.myStudents || []
    return myStudents.filter((student) =>
      student.schoolClasses.some((schoolClass) => schoolClass.subscriptionType === this.currentSubscriptionType),
    )
  }

  get allSubscribedStudents() {
    const allStudents = this.allStudents || []
    return allStudents.filter((student) =>
      student.schoolClasses.some((schoolClass) => schoolClass.subscriptionType === this.currentSubscriptionType),
    )
  }

  get mySubscribedStudentsCount() {
    return this.mySubscribedStudents.length
  }

  get allSubscribedStudentsCount() {
    return this.allSubscribedStudents.length
  }

  /**
   * All the school classes, filtered by subscription type
   */
  get allSchoolClassesBySubscriptionType() {
    return this.model?.allSchoolClasses
      .filter((sc) => sc.subscriptionType === this.currentSubscriptionType)
      .filter(hasId)
  }

  /**
   * The school classes that belong to the current teacher, filtered by subscription type
   */
  get mySchoolClassesBySubscriptionType() {
    return this.currentTeacher.schoolClasses
      .filter((sc) => sc.subscriptionType === this.currentSubscriptionType)
      .filter(hasId)
  }

  /**
   * The school classes that a teacher or subco can select from, when creating a
   * new student.
   * Depends on whether they're a subco, because where regular teachers can only add
   * students to their own classes, subcos can add students to any class.
   */
  get schoolClassesBySubscriptionType() {
    return this.isSubco ? this.allSchoolClassesBySubscriptionType : this.mySchoolClassesBySubscriptionType
  }

  /**
   * The options for the school classes that a teacher can filter by when
   * viewing the table of students.
   * If isSubco and 'All Students' tab is selected, return all students, otherwise
   * return the teacher's/subco's students.
   */
  get schoolClassFilterOptions() {
    let schoolClassOptions
    if (this.currentTab === TAB_VALUES.ALL_STUDENTS && this.isSubco) {
      schoolClassOptions = this.schoolClassByTeacherOptions
    } else {
      schoolClassOptions = this.mySchoolClassesBySubscriptionType.map(getSchoolClassAsOption)
    }
    return schoolClassOptions.slice().sort((a, b) => a.text.localeCompare(b.text))
  }

  /**
   * The options for the school classes that a teacher can select from when creating new students, grouped by teacher.
   *
   * Builds
   * {
   *   [teacherId]: {
   *     text: 'teacher name',
   *     uid: 'teacher id',
   *     studentIds: [],
   *     studentCount: 1,
   *     value: [ // school class options for that teacher
   *       { uid, value, text }
   *     ]
   *   },
   *   ... // one for each teacher option value
   * }
   *
   * Then takes the values of that object (the teacher options), sorts by the teacher name, and returns an array
   * [
   *   {
   *     text: 'teacher name',
   *     uid: 'teacher id',
   *     ...,
   *     value: [ // school class options for that teacher
   *       { uid, value, text }
   *     ]
   *   },
   *   ... // one for each teacher option value
   * ]
   */
  get schoolClassByTeacherOptions(): TeacherOption[] {
    // transform the school classes array into a hash of teachers and their school classes
    // the hash is keyed by teacher id.
    const schoolClasses = this.schoolClassesBySubscriptionType || []

    const schoolClassesByTeacher = schoolClasses.reduce<Record<string, TeacherOption>>((acc, schoolClass) => {
      const teachersForSchoolClass = schoolClass.teachers.filter(hasId) || []
      teachersForSchoolClass.forEach(function (teacher) {
        const teacherId = teacher.id
        if (!teacherId) return
        // get or create the teacher option - keep a list of unique students in the classes this teacher teaches, for an accurate student count
        const teacherOption = acc[teacherId]
          ? { ...acc[teacherId] }
          : {
              text: teacher.fullName,
              uid: teacher.id,
              studentIds: new Set<string>(),
              studentCount: 0,
              value: [],
            }

        // add students to the teachers' student list if not already present & update count
        const classStudentIds = schoolClass.students?.filter(hasId).map((s) => s.id) ?? []
        teacherOption.studentIds = new Set([...teacherOption.studentIds, ...classStudentIds])
        teacherOption.studentCount = teacherOption.studentIds.size

        // convert school class model to select option and
        // add the school class select option as a child of the teacher option
        const classOption = getSchoolClassAsOption(schoolClass)
        classOption.uid = `${teacherId}-${schoolClass.id}`
        teacherOption.value = [...teacherOption.value, classOption]

        acc[teacherId] = teacherOption
      })
      return acc
    }, {})
    // convert the hash of teachers options into an array of teacher options
    const teachers = Object.values(schoolClassesByTeacher)
    // sort the array by text (the teacher's name)
    return teachers.sort((a, b) => a.text.localeCompare(b.text))
  }

  get groupFilterOption(): SelectOptionWithStudentCount {
    return this._groupFilterOption ?? this.defaultGroupFilterOption
  }

  groupFilterDropdownOptionTemplate = groupFilterDropdownOptionTemplate

  groupFilterDropdownSubOptionTemplate = groupFilterDropdownSubOptionTemplate

  /**
   * This option is for the group filter when the logged in user is a teacher
   * or they're a subco on the my students tab
   */
  get myStudentsOption() {
    const text = this.intl.t('manage.manageClasses.students.allStudents')
    const studentCount = this.mySubscribedStudentsCount
    return { text, value: 'my-students', studentCount, uid: 'my-students' }
  }

  /**
   * This option is for the group filter when the logged in user is a subco
   * and they're on the all students tab
   */
  get allSchoolStudentsOption() {
    const text = this.intl.t('manage.manageClasses.students.allSchoolStudents')
    const studentCount = this.allSubscribedStudentsCount
    return { text, value: 'all-school-students', studentCount, uid: 'all-school-students' }
  }

  /**
   * This option is dependent on whether the logged in user is a subco or not
   * and if so which tab they're on. It will always show the biggest group for the
   * group filter dropdown.
   */
  get defaultGroupFilterOption() {
    const subcoOnAllStudentsTab = this.isSubco && this.currentTab === TAB_VALUES.ALL_STUDENTS
    if (subcoOnAllStudentsTab) {
      return this.allSchoolStudentsOption
    } else {
      return this.myStudentsOption
    }
  }

  get staticBeforeOptions() {
    return [this.defaultGroupFilterOption]
  }

  /**
   * We filter the students first by allocation filter (allocated unallocated or all)
   */
  get recordsFilteredByAllocation() {
    if (this.allocationFilterValue === ALLOCATION_FILTER_VALUES.ALL) {
      return this.students
    }
    const showAllocated = this.allocationFilterValue === ALLOCATION_FILTER_VALUES.ALLOCATED
    return this.students.filter((student) => {
      const studentIsSelected = this.selectedIds.includes(student.id)
      const studentIsAllocated = student.isAllocatedForSubscriptionType(this.currentSubscriptionType)
      const satisfiesAllocationFilter = showAllocated === studentIsAllocated
      const shouldDisplayRecord = studentIsSelected || satisfiesAllocationFilter
      return shouldDisplayRecord
    })
  }

  /**
   *  Disabled state for the remove from class button
   *  Makes use of the fact that if only one student is selected,
   *  they will share selected classes with themselves, and so
   *  therefore the button should not be disabled.
   */
  get removeFromClassDisabled() {
    return !this.selectedStudentsShareSomeClasses
  }

  get selectedStudents() {
    return this.students.filter((student) => this.selectedIds.includes(student.id))
  }

  /**
   *  True when all of the currently selected students share at least one class.
   */
  get selectedStudentsShareSomeClasses() {
    // each item in this array is a students' school classes array - eg [[sc1, sc2], [sc1, sc3, sc4], [sc1], ...]
    const classesForEachStudent = this.selectedStudents.map((student) =>
      student.schoolClasses.filter((sc) => sc.subscriptionType === this.currentSubscriptionType),
    )
    // intersect those on id to find the classes common to all students
    return intersectionBy(...classesForEachStudent, (sc) => sc.id).length > 0
  }

  /**
   * Determines an array of class ids to filter with, based on the chosen group filter option.
   * Returns true if "all students" option selected.
   */
  get groupFilterAsClassIds() {
    const groupFilterValue = this.groupFilterOption.value

    if (isEqual(groupFilterValue, 'all-school-students')) {
      return true
    } else if (isEqual(groupFilterValue, 'my-students')) {
      return this.currentTeacher.schoolClasses.map((schoolClass) => schoolClass.id)
    } else if (Array.isArray(groupFilterValue)) {
      // teacher chosen - group filter is array of class options
      return groupFilterValue.map((classOption) => {
        if (isOptionWithStudentCount(classOption)) {
          return classOption.value
        }
      })
    } else {
      // class chosen - group filter is a class id
      return [groupFilterValue]
    }
  }

  /**
   * We filter the allocation filtered records by group filter
   */
  get recordsFilteredByGroup() {
    const data = this.recordsFilteredByAllocation || []
    const { groupFilterAsClassIds } = this

    if (groupFilterAsClassIds === true) return data

    return data.filter((student) => {
      // if it's selected, keep it even if it doesn't match the filter
      if (this.selectedIds.includes(student.id)) return true

      return student.schoolClasses.some((sc) => sc.id && groupFilterAsClassIds.includes(sc.id))
    })
  }

  /**
   * Next we filter the group filtered records by free-form text entry
   */
  get recordsFilteredByText() {
    const data = this.recordsFilteredByGroup
    const trimmedTextFilter = this.textFilter.trim()
    // if no filters are applied, return all data
    if (isEmpty(trimmedTextFilter)) return data

    return data.filter((item) => {
      // if it's selected, keep it even if it doesn't match the filter
      if (this.selectedIds.includes(item.id)) return true

      const content = [
        item.firstName,
        item.lastName,
        item.login,
        item
          .schoolClassesFor(this.currentSubscriptionType)
          .map((sc) => sc.name)
          .join(' '),
        this.gradeSets.findByPosition(item.gradePosition)?.fullName ?? '',
      ].join(' ')

      return content.toLowerCase().includes(trimmedTextFilter.toLowerCase())
    })
  }

  get removeFromClassesTooltipContent() {
    const noSelectedStudents = this.selectedStudents?.length === 0
    const { selectedStudentsShareSomeClasses } = this

    if (noSelectedStudents) {
      return this.intl.t('manage.manageClasses.students.bulkActions.removeFromClass.noneSelectedTooltipContent')
    }
    if (!selectedStudentsShareSomeClasses) {
      return this.intl.t(
        'manage.manageClasses.students.bulkActions.removeFromClass.sharedClassesRequirementTooltipContent',
      )
    }

    return ''
  }

  // Contains the results of all filtering operations
  get data() {
    return this.recordsFilteredByText
  }

  get defaultSortOrder() {
    return ['firstName', 'lastName', 'gradePosition', 'login'].map((columnProp) => {
      return { key: columnProp }
    })
  }

  /**
   * Set multiple grades task
   */
  setMultipleGradesTask = task({ drop: true }, async (studentsToUpdate: Student[], newGradePosition: number) => {
    studentsToUpdate.forEach((student: Student) => {
      student.gradePosition = newGradePosition
    })
    try {
      // the students' bulk manager will handle this in bulk, and batch those requests
      const savePromises = studentsToUpdate.map((student: Student) => student.save({ adapterOptions: { bulk: true } }))
      await Promise.all(savePromises)
      const count = this.selectedIds.length
      const successMessage = this.intl.t('manage.setMultipleGrades.success', { count })
      this.flashQueue.addSuccess({ subtitle: successMessage })
      // unselect all students +  and close the modal
      this.unselectAllStudents()
      runTask(this, () => this.showEditGradeModal(false), 1000)
    } catch (error) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('failed to set multiple grades', error)
      } else {
        const count = this.selectedIds.length
        const message = this.intl.t('manage.setMultipleGrades.error', { count })
        const { selectedStudents } = this
        selectedStudents.forEach((student) => student.rollbackAttributes())
        this.flashQueue.addFail({ subtitle: message })
      }
    }
  })

  /**
   * Set multiple passwords task
   */
  setMultiplePasswordsTask = task({ drop: true }, async (studentsToUpdate: Student[], newPassword: string) => {
    studentsToUpdate.forEach((student: Student) => {
      student.plainPassword = newPassword
    })

    try {
      // the students' bulk manager will handle this in bulk, and batch those requests
      const savePromises = studentsToUpdate.map((student: Student) => student.save({ adapterOptions: { bulk: true } }))
      await Promise.all(savePromises)
      const count = this.selectedIds.length
      const successMessage = this.intl.t('manage.setMultiplePasswords.success', { count })
      this.flashQueue.addSuccess({ subtitle: successMessage })
      // unselect all students +  and close the modal
      this.unselectAllStudents()
      runTask(this, () => (this.editPasswordModalOpen = false), 1000)
    } catch (error) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('failed to set multiple passwords', error)
      } else {
        const count = this.selectedIds.length
        const message = this.intl.t('manage.setMultiplePasswords.error', { count })
        const { selectedStudents } = this
        selectedStudents.forEach((student) => student.rollbackAttributes())
        this.flashQueue.addFail({ subtitle: message })
      }
    }
  })

  /**
   * Set student ids in localStorage, then open the login-cards page in a new window / tab. That page will read the ids
   * from localStorage and render the login cards in a print-optimized style.
   *
   * We use localStorage (persisted until it is cleared) rather than sessionStorage (stored until tab is closed) because
   * we need to access it from the newly opened window / tab
   */
  @action
  printStudentLogins(students: Student[]) {
    const studentIds = students.map((student) => student.id).join(',')
    try {
      // can happen on Apple browsers where this permission is disabled
      if (!window.localStorage) throw new DOMException('localStorage is falsey', 'SecurityError')

      window.localStorage.setItem('teacher-ui-client.login-cards.student-ids', studentIds)

      const url = this.router.urlFor('login-cards', { queryParams: { subscriptionType: this.currentSubscriptionType } })
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (error) {
      this.flashQueue.addFail({ subtitle: this.intl.t('manage.manageClasses.students.bulkAction.printLogins.error') })
      if (error instanceof DOMException && error.name === 'SecurityError') {
        // user may have configured browser to prevent the page from persisting data. Don't report these.
      } else {
        this.log.error(
          'unable to set value in local storage for student login cards (via manage students)',
          error ?? '',
        )
      }
    }
  }

  _deselectAll() {
    this.selectedIds = []
  }

  _removeSelectedIds(students: Student[]) {
    const idsToRemove = students.map((s) => s.id)
    this.selectedIds = this.selectedIds.filter((id) => !idsToRemove.includes(id))
  }

  resetAllocationFilterValue() {
    this.allocationFilterValue = ALLOCATION_FILTER_VALUES.ALL
  }

  /**
   * Will only remove students from school classes of the current subscription type.
   * @param {Array<StudentModel>} studentsToRemove
   * @returns {Promise<Array>}
   */
  _removeStudentsFromClasses(studentsToRemove: Student[]) {
    const allSchoolClasses = this.model?.allSchoolClasses

    let classesToSave: SchoolClass[] = []
    studentsToRemove.forEach((student) => {
      const studentClassIds = student.schoolClasses.map((item) => item.id)
      const allSchoolClassesForStudent = allSchoolClasses.filter((sc) => studentClassIds.includes(sc.id))
      const { keepInClasses = [], removeFromClasses = [] } = groupBy(allSchoolClassesForStudent, (sc) => {
        return sc.subscriptionType === this.currentSubscriptionType ? 'removeFromClasses' : 'keepInClasses'
      })

      student.schoolClasses = keepInClasses
      classesToSave = unionBy([...classesToSave, ...removeFromClasses], 'id')
    })
    return all(classesToSave.map((sc) => sc.save()))
  }

  @tracked
  removeFromClassModalOpen = false

  @action
  setRemoveFromClassModal(open: boolean) {
    this.removeFromClassModalOpen = open
  }

  @action
  async submitRemoveFromClassModal(studentsToRemove: Student[], schoolClassId: string) {
    const schoolClass = this.store.peekRecord<SchoolClass>('school-class', schoolClassId)
    if (!schoolClass) {
      return
    }
    if (schoolClass?.students) {
      schoolClass.students = schoolClass.students.filter((s) => !studentsToRemove.includes(s))
      await schoolClass.save()
    }

    // remove student IDs from the list of selected stduents
    this._removeSelectedIds(studentsToRemove)
  }

  @action
  changeTab(tabValue: string) {
    this.currentTab = tabValue
    this._deselectAll()
    if (tabValue !== TAB_VALUES.ALL_STUDENTS) {
      this.resetAllocationFilterValue()
    }
    // Ensure the dropdown has the correct options for the active tab
    this.resetGroupFilterOption()
  }

  @action
  resetGroupFilterOption() {
    this.setGroupFilterOption(undefined)
  }

  @action
  setTextFilter(textFilter: string) {
    this.textFilter = textFilter
    this.currentPage = 1
  }

  @action
  setGroupFilterOption(option?: SelectOptionWithStudentCount) {
    this._groupFilterOption = option
    this.currentPage = 1
  }

  @action
  showEditPasswordModal(open: boolean) {
    this.editPasswordModalOpen = open
  }

  @action
  showEditGradeModal(open: boolean) {
    this.editGradeModalOpen = open
  }

  @action
  showRemoveFromAllClassesModal(open: boolean) {
    this.removeFromAllClassesModalOpen = open
  }

  @action
  async submitRemoveFromAllClassesModal(studentsToRemove: Student[]) {
    this._removeSelectedIds(studentsToRemove)
    try {
      await this._removeStudentsFromClasses(studentsToRemove)
    } catch (error) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('failed to remove students from all classes', error)
      } else {
        throw error
      }
    }
  }

  /**
   * The Show All | `Allocated` | `Unallocated` filter button group has been toggled.
   */
  @action
  updateAllocationFilterValue(_optionSet: unknown, value: string) {
    this._deselectAll()
    if (value === ALLOCATION_FILTER_VALUES.UNALLOCATED) {
      // Unassigned students don't have a school class or teacher hence the school class and teacher filters are
      // reset when the `Show Unallocated` filter is activated.
      this.allocationFilterValue = value
    } else {
      this.allocationFilterValue = value
    }
    this.currentPage = 1
  }

  /**
   * Unselect all selected students and also close the edit student drawer if open
   * @returns {undefined}
   */
  @action
  unselectAllStudents() {
    this.selectedIds = []
    void this.router.transitionTo('manage.students')
  }

  @action
  exportAllStudentData() {
    this.submitExportStudentData(this.students)
  }

  @action
  submitExportStudentData(students: Student[]) {
    const csvDataArray = this.studentCsvMaker.studentDataCsvArray(students)
    const messageKey = 'manage.manageClasses.students.bulkActions.exportStudents.success'
    const successMessage = this.intl.t(messageKey, { count: students.length })

    this.flashQueue.addSuccess({ subtitle: successMessage })
    this.fileDownload.downloadAsCsv(csvDataArray)
  }

  @action
  onDeleteStudentsSuccess() {
    this.selectedIds = []
    // closes any student rows that might be open
    void this.router.transitionTo('manage.students')
    // the endpoint will have removed students from classes as well, so get updated school class records
    void this.router.refresh('manage.students')
  }

  @action
  openDeleteStudentsModal() {
    this.deleteStudentsModalOpen = true
  }

  @action
  closeDeleteStudentsModal() {
    this.deleteStudentsModalOpen = false
  }
}
